import React from "react";
import { ArrowRight, Sparkles, Activity, Droplet } from "lucide-react";

const openReviveBuy = (flavour) =>
  window.dispatchEvent(
    new CustomEvent("revive:open-buy", { detail: flavour ? { flavour } : {} }),
  );

/* User-provided REVIVE imagery */
const IMG_INGREDIENTS =
  "https://customer-assets.emergentagent.com/job_lifestyle-essentials-3/artifacts/fpeix0ke_image.png";
const IMG_BENEFITS =
  "https://customer-assets.emergentagent.com/job_lifestyle-essentials-3/artifacts/rgp4xy9u_image.png";
const IMG_HERO =
  "https://customer-assets-agu9un31.emergentagent.net/job_lifestyle-essentials-3/artifacts/uo6vn1on_image.png";
const IMG_FLAVORS_ROW1 =
  "https://customer-assets.emergentagent.com/job_lifestyle-essentials-3/artifacts/fugxg3ni_image.png";
const IMG_FLAVORS_ROW2 =
  "https://customer-assets.emergentagent.com/job_lifestyle-essentials-3/artifacts/jo1jkn77_image.png";
const IMG_WATERMELON =
  "https://customer-assets.emergentagent.com/job_lifestyle-essentials-3/artifacts/w282pkjq_image.png";

const FLAVORS = [
  { name: "Raw Unflavoured", color: "#9CA3AF" },
  { name: "Apple", color: "#3FB54A" },
  { name: "Blackcurrant", color: "#7E3FB5" },
  { name: "Blood Orange", color: "#E85020" },
  { name: "Cucumber Mint", color: "#9FDDC4" },
  { name: "Lemon Lime", color: "#C8E22E" },
  { name: "Peach", color: "#F1B98E" },
  { name: "Raspberry", color: "#E94B6A" },
  { name: "Watermelon", color: "#F47A8E" },
];

const Spec = ({ value, label }) => (
  <div className="flex flex-col gap-1">
    <span className="font-display font-black text-terracotta text-2xl md:text-3xl leading-none">
      {value}
    </span>
    <span className="overline text-bone/55">{label}</span>
  </div>
);

export const ReviveStory = () => {
  return (
    <section
      id="revive"
      data-testid="revive-story-section"
      className="bg-ink text-bone py-24 md:py-32 overflow-hidden"
    >
      {/* ── Hero band ────────────────────────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <div className="overline text-terracotta mb-5">
              Product Deep Dive · No. 01
            </div>
            <h2 className="font-display font-black text-5xl md:text-7xl tracking-tight leading-[0.95]">
              REVIVE
              <br />
              <span className="font-wordmark italic font-semibold text-bone/80 text-3xl md:text-4xl">
                Daily Electrolytes
              </span>
            </h2>
            <p className="mt-6 text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
              The perfect hydration formula. Built for health, fasting and
              performance — and now stocked in the UAE through Thrive by Madsen.
            </p>

            {/* Specs row */}
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-bone/15 pt-6">
              <Spec value="1000mg" label="Sodium" />
              <Spec value="200mg" label="Potassium" />
              <Spec value="100mg" label="Magnesium" />
            </div>

            <div className="mt-10 flex items-center gap-6">
              <button
                type="button"
                onClick={() => openReviveBuy()}
                data-testid="revive-shop-cta"
                className="group inline-flex items-center gap-2 bg-terracotta hover:bg-bone hover:text-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm"
              >
                Shop REVIVE
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <span className="overline text-bone/60">
                No sugar · No carbs · No calories
              </span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative aspect-[16/10] rounded-sm overflow-hidden bg-ink border border-bone/10">
              <img
                src={IMG_HERO}
                alt="REVIVE — The perfect hydration formula"
                data-testid="revive-hero-image"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* ── Ingredients story ────────────────────────────────── */}
        <article
          data-testid="revive-ingredients-block"
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center border-t border-bone/15 pt-16 md:pt-24"
        >
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="relative aspect-[5/4] rounded-sm overflow-hidden bg-black">
              <img
                src={IMG_INGREDIENTS}
                alt="Carefully chosen REVIVE ingredients — sodium, potassium, magnesium"
                className="w-full h-full object-contain"
                loading="lazy"
              />
              {/* Cover the baked-in "Learn More" pill */}
              <div
                aria-hidden="true"
                data-testid="revive-ingredients-learnmore-cover"
                className="absolute rounded-full bg-black pointer-events-none"
                style={{
                  left: "22%",
                  top: "76%",
                  width: "20%",
                  height: "11%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Sparkles className="w-6 h-6 text-terracotta mb-5" strokeWidth={1.5} />
            <div className="overline text-terracotta mb-3">Chapter 01</div>
            <h3 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.02]">
              Carefully chosen
              <br />
              <span className="font-wordmark italic font-semibold text-bone/85">
                ingredients.
              </span>
            </h3>
            <div className="mt-6 space-y-5 text-bone/75 text-base md:text-lg leading-relaxed">
              <p>
                There are no shortcuts to a great product. We source ingredients
                from around the world. No chemicals, no colouring, no additives —
                not even anti-caking agents. Nothing artificial either.
              </p>
              <p>
                Our electrolytes come from{" "}
                <span className="text-bone">sodium chloride</span>,{" "}
                <span className="text-bone">potassium chloride</span> and{" "}
                <span className="text-bone">magnesium malate</span>.
              </p>
              <p>
                Our salt is from the ancient{" "}
                <span className="text-terracotta italic font-wordmark">
                  Kalahari
                </span>{" "}
                — pristine and unpolluted — through a special partnership with
                Oryx Desert Salt.
              </p>
            </div>
          </div>
        </article>

        {/* ── Benefits story ───────────────────────────────────── */}
        <article
          data-testid="revive-benefits-block"
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center border-t border-bone/15 pt-16 md:pt-24 mt-16 md:mt-24"
        >
          <div className="lg:col-span-5">
            <Activity className="w-6 h-6 text-terracotta mb-5" strokeWidth={1.5} />
            <div className="overline text-terracotta mb-3">Chapter 02</div>
            <h3 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.02]">
              Benefits you
              <br />
              <span className="font-wordmark italic font-semibold text-bone/85">
                will feel.
              </span>
            </h3>
            <div className="mt-6 space-y-5 text-bone/75 text-base md:text-lg leading-relaxed">
              <p>
                Hydration has a big impact on your body and mind. Electrolytes
                are needed to maintain fluid volume, keep muscles pumping, and
                nerves firing — including your billions of brain neurons.
              </p>
              <p className="text-bone">
                REVIVE helps you feel better, function better, and perform
                better.
              </p>
            </div>

            <ul className="mt-8 grid grid-cols-2 gap-3" data-testid="revive-benefits-list">
              {[
                "Recovery",
                "Anti-cramp",
                "Cognitive function",
                "Athletic performance",
                "Big nights",
                "Long flights",
              ].map((b) => (
                <li
                  key={b}
                  className="flex items-center gap-2 border border-bone/15 bg-bone/[0.03] px-3 py-2.5 rounded-sm"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                  <span className="font-display font-semibold text-bone text-sm">
                    {b}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="relative aspect-[5/4] rounded-sm overflow-hidden bg-black">
              <img
                src={IMG_BENEFITS}
                alt="REVIVE benefits you will feel — recovery, anti-cramp, cognitive function, athletic performance, big nights, long flights"
                className="w-full h-full object-contain"
                loading="lazy"
              />
              {/* Cover the baked-in "Learn More" pill */}
              <div
                aria-hidden="true"
                data-testid="revive-benefits-learnmore-cover"
                className="absolute rounded-full bg-black pointer-events-none"
                style={{
                  left: "23%",
                  top: "69%",
                  width: "18%",
                  height: "8%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </div>
          </div>
        </article>

        {/* ── Flavour lineup ───────────────────────────────────── */}
        <article
          data-testid="revive-flavors-block"
          className="border-t border-bone/15 pt-16 md:pt-24 mt-16 md:mt-24"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <Droplet className="w-6 h-6 text-terracotta mb-5" strokeWidth={1.5} />
              <div className="overline text-terracotta mb-3">Chapter 03</div>
              <h3 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.02]">
                Nine flavours.
                <br />
                <span className="font-wordmark italic font-semibold text-bone/85">
                  All 30-sachet boxes.
                </span>
              </h3>
            </div>
            <p className="md:max-w-sm text-bone/65 text-base leading-relaxed">
              From the unflavoured Raw — for purists and mixologists — to crowd-
              pleasing Apple, Lemon Lime, Blood Orange and the just-arrived
              Watermelon. One sachet, one serving, one box of 30.
            </p>
          </div>

          {/* Big flavour image row 1 */}
          <div
            className="relative rounded-sm overflow-hidden bg-bone p-4 md:p-8"
            data-testid="revive-flavors-row1"
          >
            <img
              src={IMG_FLAVORS_ROW1}
              alt="REVIVE flavours — Raw Unflavoured, Apple, Blackcurrant, Blood Orange"
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>

          {/* Big flavour image row 2 */}
          <div
            className="relative rounded-sm overflow-hidden bg-bone p-4 md:p-8 mt-4"
            data-testid="revive-flavors-row2"
          >
            <img
              src={IMG_FLAVORS_ROW2}
              alt="REVIVE flavours — Cucumber Mint, Lemon Lime, Peach, Raspberry"
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>

          {/* New flavour highlight — Watermelon */}
          <div
            className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-4"
            data-testid="revive-watermelon-block"
          >
            <div className="md:col-span-7 relative rounded-sm overflow-hidden bg-bone p-4 md:p-8 flex items-center justify-center">
              <img
                src={IMG_WATERMELON}
                alt="REVIVE Watermelon 30 sachet box — newest flavour"
                className="w-full max-h-[460px] object-contain"
                loading="lazy"
              />
            </div>
            <div className="md:col-span-5 bg-terracotta text-white rounded-sm p-8 md:p-10 flex flex-col justify-center">
              <span className="overline text-white/80 mb-4">New · No. 09</span>
              <h4 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[1.05]">
                Watermelon.
              </h4>
              <p className="mt-4 font-wordmark italic text-2xl md:text-3xl leading-snug text-white/95">
                The summer flavour, year-round.
              </p>
              <p className="mt-5 text-white/85 text-base leading-relaxed">
                Just landed in the Thrive by Madsen range — same 1000 mg sodium,
                200 mg potassium, 100 mg magnesium. No sugar, no carbs, no
                calories.
              </p>
              <button
                type="button"
                onClick={() => openReviveBuy("Watermelon")}
                data-testid="watermelon-cta"
                className="mt-7 inline-flex items-center gap-2 bg-bone text-ink hover:bg-ink hover:text-bone transition-colors duration-300 px-5 py-3 rounded-sm font-display font-semibold text-sm w-fit"
              >
                Shop the box
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Flavour swatch strip */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="revive-flavor-swatches">
            {FLAVORS.map((f) => (
              <div
                key={f.name}
                className="flex items-center gap-2 border border-bone/15 bg-bone/[0.03] px-3 py-2 rounded-sm"
              >
                <span
                  className="w-3 h-3 rounded-full border border-bone/20"
                  style={{ backgroundColor: f.color }}
                />
                <span className="font-display font-semibold text-bone text-sm">
                  {f.name}
                </span>
                <span className="overline text-bone/45">30’s</span>
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
};
